// Собственик: списък на имотите (само четене).
// Замразен имот (прекратено управление) не е задънена улица — собственикът
// го връща на пропърти мениджъра или го поема сам (тогава става наемодател).

import React, { useCallback, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import { api, money } from '../../api';
import { useAuth } from '../../AuthContext';
import { confirmAction, showError, showSuccess } from '../../toast';
import { Badge, Button, Card, COLORS, Muted, Screen, Title } from '../../ui';
import { cityLabel } from '../../geo';
import { t } from '../../i18n';

export default function OwnerApartmentsScreen({ navigation }: any) {
  const { refreshUser } = useAuth();
  const [apartments, setApartments] = useState<any[]>([]);
  const [frozen, setFrozen] = useState<any[]>([]);

  const load = useCallback(() => {
    api('/owner/apartments').then((r) => setApartments(r.apartments)).catch(() => {});
    api('/management-contracts/frozen').then((r) => setFrozen(r.apartments)).catch(() => {});
  }, []);
  useFocusEffect(load);

  const frozenById = new Map(frozen.map((f) => [f.id, f]));

  const restore = (apartmentId: string, mode: 'AGENCY' | 'SELF', agencyName?: string) => {
    const [title, message, action] =
      mode === 'AGENCY'
        ? [t('Връщане към пропърти мениджърът'), t('Да възобновим ли управлението към {name}?', { name: agencyName ?? '' }), t('Върни')]
        : [
            t('Поемате имота сам'),
            t('Ще управлявате имота сами: профилът ви става наемодателски и започва пробен период на абонамента. Другите ви имоти под чуждо управление си остават непроменени.'),
            t('Поемам го'),
          ];

    confirmAction(title, message, action, async () => {
      try {
        const res = await api(`/management-contracts/apartments/${apartmentId}/restore`, { body: { mode } });
        if (res.becameLandlord) {
          showSuccess(t('Вече сте наемодател на този имот. Отваряме наемодателския изглед…'));
          await refreshUser(); // ролята се смени → навигацията се пренарежда
        } else {
          showSuccess(t('Управлението е възобновено към пропърти мениджърът.'));
        }
        load();
      } catch (e: any) {
        showError(e.message);
      }
    });
  };

  return (
    <Screen>
      <Title>{t('Моите имоти')}</Title>
      <Muted>{t('Режим само за преглед — имотите се управляват от пропърти мениджъра.')}</Muted>
      <View style={{ height: 12 }} />
      {apartments.length === 0 && (
        <Card>
          <Muted>{t('Няма имоти. Когато пропърти мениджър ви покани, те ще се появят тук.')}</Muted>
        </Card>
      )}
      {apartments.map((a) => {
        const f = frozenById.get(a.id);
        const isFrozen = a.status === 'FROZEN';
        return (
          <Card key={a.id} style={isFrozen ? { borderColor: COLORS.danger, borderWidth: 2 } : {}}>
            <Pressable onPress={() => navigation.navigate('OwnerApartmentDetail', { id: a.id })}>
              <Text style={{ fontSize: 17, fontWeight: '600', color: COLORS.text }}>{a.address}</Text>
              <Muted>{cityLabel(a.city, a.region)}{a.agency && !isFrozen ? ` · ${a.agency} (${a.managementPercent}%)` : ''}</Muted>
              <View style={{ marginTop: 8, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                {isFrozen ? (
                  <Badge text={t('Замразен (прекратено управление)')} color={COLORS.danger} />
                ) : a.currentTenancy ? (
                  <>
                    <Badge text={t('Наемател: {name}', { name: a.currentTenancy.tenant ?? t('чака регистрация') })} color={COLORS.success} />
                    <Text style={{ fontWeight: '600', color: COLORS.text }}>{t('{amount}/мес.', { amount: money(a.currentTenancy.monthlyRentCents) })}</Text>
                  </>
                ) : (
                  <Badge text={t('Свободен')} color={COLORS.muted} />
                )}
              </View>
            </Pressable>

            {/* Замразен имот → избор как да продължи */}
            {isFrozen && (
              <View style={{ marginTop: 10, borderTopWidth: 1, borderTopColor: COLORS.border, paddingTop: 10 }}>
                <Muted>{t('Изберете как да продължите с този имот:')}</Muted>
                {f?.lastContract && (
                  <Button
                    title={t('Върни на {name} ({pct}%)', { name: f.lastContract.agency.name, pct: f.lastContract.managementPercent })}
                    variant="secondary"
                    onPress={() => restore(a.id, 'AGENCY', f.lastContract.agency.name)}
                  />
                )}
                <Button title={t('Поемам го сам')} onPress={() => restore(a.id, 'SELF')} />
                <Muted>
                  {t('Ако го поемете сам, профилът ви става наемодателски и започва пробен период на абонамента.')}
                </Muted>
              </View>
            )}
          </Card>
        );
      })}
    </Screen>
  );
}
